import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, ArrowRight, Power, PowerOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ProvinciaPicker from "@/components/ProvinciaPicker";
import AddressItalyFields from "@/components/AddressItalyFields";
import { useConfirm } from "@/components/ConfirmProvider";

// Sprint 4SED.HF — helper estrazione messaggio errore safe per React
const errMsg = (e, fallback = "Errore") => {
  const d = e?.response?.data?.detail;
  if (d == null) return fallback;
  if (typeof d === "string") return d;
  if (typeof d === "object") return d.detail || d.error || fallback;
  return fallback;
};

function emptyForm() {
  return {
    ragione_sociale: "", nome_breve: "", partita_iva: "", codice_fiscale: "",
    reg_imprese: "",
    indirizzo_legale: { via: "", cap: "", comune: "", provincia: "" },
    telefono: "", email: "", sito_web: "",
  };
}

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const load = () => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Sprint 4SED.F — Disattiva/Riattiva azienda
  const handleToggleAttiva = async (c) => {
    const isDeactivating = c.attiva !== false;
    const ok = await confirm({
      variant: "approve",
      title: isDeactivating ? "Disattiva azienda" : "Riattiva azienda",
      message: isDeactivating
        ? `Disattivare "${c.nome_breve || c.name}"? Nessuno potrà operare sui suoi dati, ma tutti i riferimenti restano. Riattivabile in qualsiasi momento.`
        : `Riattivare "${c.nome_breve || c.name}"?`,
      confirmLabel: isDeactivating ? "SI, disattiva" : "SI, riattiva",
    });
    if (!ok) return;
    try {
      await api.patch(`/companies/${c.id}`, { attiva: !isDeactivating });
      toast.success(isDeactivating ? "Azienda disattivata" : "Azienda riattivata");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    }
  };

  // Sprint 4SED.F — Hard delete (solo 0 dati)
  const handleDelete = async (c) => {
    const ok = await confirm({
      variant: "delete",
      title: "Elimina definitivamente",
      message: `Eliminare DEFINITIVAMENTE "${c.nome_breve || c.name}"? L'operazione è irreversibile.`,
      confirmLabel: "SI, elimina",
    });
    if (!ok) return;
    try {
      await api.delete(`/companies/${c.id}`, { data: { motivo: "hard delete da UI" } });
      toast.success("Azienda eliminata definitivamente");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore eliminazione"), { duration: 7000 });
    }
  };

  const submit = async () => {
    if (!/^\d{11}$/.test(form.partita_iva)) {
      toast.error("Partita IVA: 11 cifre numeriche");
      return;
    }
    setSaving(true);
    try {
      await api.post("/companies", form);
      toast.success("Azienda creata");
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <Building2 className="h-6 w-6 text-blue-600" /> Aziende
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestione aziende</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="company-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Nuova azienda
        </Button>
      </div>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Nome breve</th>
                <th className="px-4 py-2.5">Ragione sociale</th>
                <th className="px-4 py-2.5">P.IVA</th>
                <th className="px-4 py-2.5 text-right">Sedi</th>
                <th className="px-4 py-2.5 text-right">Stock</th>
                <th className="px-4 py-2.5 text-right">In gestione</th>
                <th className="px-4 py-2.5 text-right">Totale auto</th>
                <th className="px-4 py-2.5">Attiva</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {companies.map((c) => {
                const isActive = c.attiva !== false;
                const hasData = (c.veicoli_totale ?? 0) > 0 || (c.sites_count ?? 0) > 0;
                return (
                <tr key={c.id} className={`hover:bg-slate-50 ${!isActive ? "bg-slate-50/60" : ""}`} data-testid={`company-row-tr-${c.id}`}>
                  <td className={"px-4 py-3 font-medium " + (isActive ? "text-slate-900" : "text-slate-500")}>
                    <div className="flex items-center gap-2">
                      {c.codice_azienda && (
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100"
                              data-testid={`company-codice-${c.id}`}>
                          {c.codice_azienda}
                        </span>
                      )}
                      <span>{c.nome_breve || c.name || "—"}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 truncate max-w-[260px]">{c.ragione_sociale || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.partita_iva || "—"}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{c.sites_count ?? 0}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-700" data-testid={`company-stock-${c.id}`}>{c.veicoli_stock ?? 0}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-amber-700" data-testid={`company-in-gestione-${c.id}`}>{c.veicoli_in_gestione ?? 0}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-900" data-testid={`company-totale-${c.id}`}>{c.veicoli_totale ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
                          data-testid={`company-stato-${c.id}`}>
                      {isActive ? "Attiva" : "Disattivata"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-3 justify-end">
                      <button
                        onClick={() => handleToggleAttiva(c)}
                        className={isActive ? "text-amber-600 hover:text-amber-700" : "text-emerald-600 hover:text-emerald-700"}
                        title={isActive ? "Disattiva azienda" : "Riattiva azienda"}
                        data-testid={`company-toggle-attiva-${c.id}`}
                      >
                        {isActive ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(c)}
                        disabled={hasData}
                        className={hasData ? "text-rose-300 cursor-not-allowed" : "text-rose-600 hover:text-rose-700"}
                        title={hasData
                          ? "Non eliminabile: contiene dati. Disattivala (l'export massivo arriverà col cluster Backup)."
                          : "Elimina definitivamente"}
                        data-testid={`company-delete-${c.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <Link to={`/companies/${c.id}`} className="text-blue-600 hover:underline text-xs flex items-center gap-1" data-testid={`company-row-${c.id}`}>
                        Apri <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </td>
                </tr>
                );
              })}
              {companies.length === 0 && (
                <tr><td colSpan={9} className="text-center py-8 text-slate-400">Nessuna azienda</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nuova azienda</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            {[
              ["nome_breve", "Nome breve *"], ["ragione_sociale", "Ragione sociale *"],
              ["partita_iva", "Partita IVA * (11 cifre)"], ["codice_fiscale", "Codice fiscale *"],
              ["reg_imprese", "Reg. Imprese"],
              ["telefono", "Telefono"], ["email", "Email"], ["sito_web", "Sito web"],
            ].map(([key, label]) => (
              <div key={key} className={key === "ragione_sociale" ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{label}</Label>
                <Input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={`company-input-${key}`} />
              </div>
            ))}
            <div className="sm:col-span-2 pt-2 border-t">
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">
                Indirizzo legale (Italia)
              </Label>
              <AddressItalyFields
                value={form.indirizzo_legale}
                onChange={(addr) => setForm({ ...form, indirizzo_legale: addr })}
                testIdPrefix="company-addr"
                compact
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={saving} data-testid="company-create-submit">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
